import { createContext, ReactNode, useContext, useState } from 'react';
import LocationDetails from './Models/LocationDetails';
import LocationPrediction from './Models/LocationPrediction';

export interface LocationContextInterface extends LocationContextState {
    setInputValue: (value: string) => void;
    setLocationDetails: (value: LocationDetails) => void;
    setLocationPredictions: (value: LocationPrediction[]) => void;
    reset: () => void;
    clear: () => void;
}

export interface LocationContextState {
    inputValue: string;
    options: readonly LocationPrediction[];
    open: boolean;
    locationDetails: LocationDetails | null;
}

export const DefaultLocationContextState: LocationContextState = {
    inputValue: '',
    options: [],
    open: false,
    locationDetails: null,
};

export const LocationContext = createContext<LocationContextInterface | undefined>(undefined);
const LocationContextProvider = LocationContext.Provider;

interface LocationProviderProps {
    children: ReactNode;
    initialLocationDetails: LocationDetails | null;
}

export const LocationProvider = ({ children, initialLocationDetails }: LocationProviderProps) => {
    const [state, setState] = useState<LocationContextState>({
        ...DefaultLocationContextState,
        inputValue: initialLocationDetails?.description ?? '',
        locationDetails: initialLocationDetails,
    });

    const { inputValue, options, open, locationDetails } = state;

    const setInputValue = (value: string) => {
        setState((prev) => {
            return {
                ...prev,
                inputValue: value,
                open: true,
            };
        });
    };

    const setLocationDetails = (value: LocationDetails) => {
        setState((prev) => {
            return {
                ...prev,
                locationDetails: value,
            };
        });
    };

    const setLocationPredictions = (value: LocationPrediction[]) => {
        setState((prev) => {
            return {
                ...prev,
                options: value,
            };
        });
    };

    const reset = () => {
        setState((prev) => {
            return {
                ...prev,
                open: false,
            };
        });
    };
    const clear = () => {
        setState({ ...DefaultLocationContextState });
    };

    return (
        <LocationContextProvider
            value={{
                inputValue,
                options,
                open,
                locationDetails,

                setInputValue,
                setLocationDetails,
                setLocationPredictions,
                reset,
                clear,
            }}
        >
            {children}
        </LocationContextProvider>
    );
};

export function useLocationContext() {
    const context = useContext(LocationContext);
    if (!context) {
        throw new Error('LocationContext context should be used inside LocationContextProvider');
    }
    return context;
}
